import React, { useState, useEffect } from "react";
import { apiGet } from "@/contexts/AuthContext";
import { Nav } from "@/pages/Dashboard";
import { FileText, Download, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = { worksheet: 'Worksheet', cheatsheet: 'Cheat Sheet', guide: 'Guide' };

export default function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    apiGet(`${API}/resources`)
      .then(r => setResources(r.data)).catch(console.error);
  }, []);

  const filtered = resources.filter(r => {
    const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = !categoryFilter || r.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const categories = [...new Set(resources.map(r => r.category))];

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      <Nav />
      <main className="px-6 sm:px-12 py-10 max-w-5xl mx-auto">
        <h1 className="text-4xl sm:text-5xl mb-3 fade-in" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Resource Library</h1>
        <p className="text-base mb-10 fade-in" style={{ color: '#5B665E' }}>Worksheets, guides, and cheat sheets to support your journey.</p>

        {/* Search & Filter */}
        <div className="flex flex-wrap gap-3 mb-10 fade-in stagger-1" data-testid="resource-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#5B665E' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} className="earthy-input pl-10" placeholder="Search resources..." data-testid="resource-search-input" />
          </div>
          <button onClick={() => setCategoryFilter(null)} className={`earthy-badge cursor-pointer ${!categoryFilter ? 'badge-active' : 'badge-locked'}`} data-testid="filter-all-resources">All</button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setCategoryFilter(cat)} className={`earthy-badge cursor-pointer capitalize ${categoryFilter === cat ? 'badge-active' : 'badge-locked'}`} data-testid={`filter-${cat}`}>
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((r, i) => (
            <div key={r.id} className="earthy-card fade-in" style={{ animationDelay: `${i * 0.04}s`, opacity: 0 }} data-testid={`resource-${r.id}`}>
              <div className="flex items-center gap-2 mb-4">
                <FileText size={18} strokeWidth={1.5} style={{ color: '#A67C52' }} />
                <span className="earthy-badge badge-locked text-[9px]">{TYPE_LABELS[r.type] || r.type}</span>
                <span className="earthy-badge badge-active text-[9px]">Week {r.week_num}</span>
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>{r.title}</h3>
              <p className="text-sm mb-5" style={{ color: '#5B665E' }}>{r.description}</p>
              <button className="earthy-btn earthy-btn-secondary text-xs py-2 px-4 flex items-center gap-1.5" data-testid={`download-${r.id}`}>
                <Download size={14} strokeWidth={1.5} /> Download
              </button>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="earthy-card text-center py-12" data-testid="no-resources">
            <p className="text-lg" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>No resources match your search.</p>
          </div>
        )}
      </main>
    </div>
  );
}
